/**
 * Parcelas CAD en el panel Registros (`GET /getmycadparcels`).
 * No mezclar con `getmylots`: un `custom_parcels.id` no es un `lots.id`.
 * @see docs/maps/registros-cad-parcels.md
 */

import { isCadSellableLot } from './cadMasterplan';
import { formatCadPrice } from './cadParcelDisplay';
import { CAD_STOCK_LABELS } from '../data/cadConstants';

export const CAD_REGISTROS_ITEM_TYPE = 'cad_parcel';
export const CAD_COLLECTION_FILTER_PREFIX = 'cad:';

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isCadCollectionFilter(value) {
  return String(value || '').startsWith(CAD_COLLECTION_FILTER_PREFIX);
}

/**
 * @param {string} slug
 * @returns {string}
 */
export function cadCollectionFilterValue(slug) {
  return `${CAD_COLLECTION_FILTER_PREFIX}${String(slug || '').trim()}`;
}

/**
 * @param {unknown} value
 * @returns {string|null}
 */
export function cadCollectionFilterSlug(value) {
  if (!isCadCollectionFilter(value)) return null;
  const slug = String(value).slice(CAD_COLLECTION_FILTER_PREFIX.length).trim();
  return slug || null;
}

/**
 * @param {unknown} row
 * @returns {boolean}
 */
export function isCadRegistrosRow(row) {
  return Boolean(row) && row.item_type === CAD_REGISTROS_ITEM_TYPE;
}

/**
 * Card: `title` comercial, o `name` del DXF si falta.
 * @param {object|null|undefined} row
 * @returns {string}
 */
export function getCadParcelCardLabel(row) {
  const title = typeof row?.title === 'string' ? row.title.trim() : '';
  if (title) return title;
  const name = typeof row?.name === 'string' ? row.name.trim() : '';
  return name || 'Lote';
}

/** Nombre de la lista CAD. Vacío si la fila no lo trae. */
export function getCadRegistrosListName(row) {
  return String(row?.project_name || row?.project_slug || '').trim();
}

/**
 * Segunda línea de la card en Registros. La lista siempre va: es lo que
 * distingue el lote de un catastral. Superficie si hay. Precio solo si es
 * mayor a 0 (US$ 0 no aporta). Stock solo si no está disponible.
 * @param {object|null|undefined} row
 * @returns {string}
 */
export function getCadRegistrosCardMeta(row) {
  const parts = [getCadRegistrosListName(row) || 'Lista'];
  const area = Number(row?.area_m2);
  if (Number.isFinite(area) && area > 0) {
    parts.push(`${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(area)} m²`);
  }
  const priceN = Number(row?.price);
  if (Number.isFinite(priceN) && priceN > 0) {
    const price = formatCadPrice(priceN, row?.currency);
    if (price && price !== '—') parts.push(price);
  }
  const stock = String(row?.stock_status || '').trim();
  if (stock && stock !== 'available' && CAD_STOCK_LABELS[stock]) {
    parts.push(CAD_STOCK_LABELS[stock]);
  }
  return parts.join(' · ');
}

/**
 * Search de Registros: title, name, project_name.
 * @param {object|null|undefined} row
 * @param {string} term
 * @returns {boolean}
 */
export function cadParcelMatchesSearch(row, term) {
  const needle = String(term || '').trim().toLowerCase();
  if (!needle) return true;
  const haystack = [row?.title, row?.name, row?.project_name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

/**
 * Pin en el mapa país: lienzo `map` y centroide WGS84. Nunca metros DXF.
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function canLocateCadParcelOnCountryMap(row) {
  if (!row || row.display_mode !== 'map') return false;
  if (row.lat == null || row.lng == null || row.lat === '' || row.lng === '') {
    return false;
  }
  const lat = Number(row.lat);
  const lng = Number(row.lng);
  return Number.isFinite(lat) && Number.isFinite(lng);
}

/**
 * Ruta visitante de lista CAD (HashRouter: `/#/lista/:id`).
 * No unificar con `/#/shared/:token` (lista catastro): son UUID/contratos distintos.
 * El slug es pretty y puede ir en `/#/lista/:slug/:id`, pero las cards/links
 * canónicos usan solo el id. Sin id no hay path usable.
 *
 * @param {{ id?: string, slug?: string, parcelId?: string, search?: string }} [opts]
 * @returns {string}
 */
export function buildShowroomPath({ id, parcelId, search = '' } = {}) {
  const trimmedId = String(id || '').trim();
  if (!trimmedId) return '/lista';

  const path = `/lista/${encodeURIComponent(trimmedId)}`;

  const params = new URLSearchParams();
  const parcel = String(parcelId || '').trim();
  if (parcel) params.set('parcel', parcel);
  const extra = String(search || '').replace(/^\?/, '');
  if (extra) {
    new URLSearchParams(extra).forEach((value, key) => {
      if (!params.has(key)) params.set(key, value);
    });
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * @param {string} [_slug] Ignorado (compat); el path va por projectId.
 * @param {string} [parcelId]
 * @param {string} [projectId]
 * @returns {string}
 */
export function buildShowroomParcelPath(_slug, parcelId, projectId) {
  return buildShowroomPath({ id: projectId, parcelId });
}

/**
 * Marcadores y filtros de parcela catastral no aplican a CAD.
 * En “Seleccionar listas”, CAD entra si no hay selección o si hay un showroom tildado.
 * @param {{ markerFilter?: string, listFilters?: unknown[], hasParcelFilters?: boolean }} [options]
 * @returns {boolean}
 */
export function cadParcelsVisibleInRegistros({
  markerFilter = 'all',
  listFilters = [],
  hasParcelFilters = false,
} = {}) {
  if (markerFilter === 'favorites' || hasParcelFilters) return false;
  const sel = Array.isArray(listFilters) ? listFilters : [];
  if (!sel.length) return true;
  return sel.some(isCadCollectionFilter);
}

/**
 * Showrooms presentes en el universo de Registros, para el mismo selector de listas.
 * @param {object[]} parcels
 * @returns {{ slug: string, name: string }[]}
 */
export function uniqueCadCollectionsFromParcels(parcels) {
  const seen = new Map();
  (Array.isArray(parcels) ? parcels : []).forEach((row) => {
    const slug = String(row?.project_slug || '').trim();
    if (!slug || seen.has(slug)) return;
    const name = typeof row?.project_name === 'string' && row.project_name.trim()
      ? row.project_name.trim()
      : slug;
    seen.set(slug, { slug, name });
  });
  return Array.from(seen.values());
}

/**
 * @param {object[]} parcels
 * @param {unknown[]} listFilters
 * @returns {object[]}
 */
export function filterCadParcelsByCollectionFilters(parcels, listFilters) {
  const rows = Array.isArray(parcels) ? parcels : [];
  const slugs = (Array.isArray(listFilters) ? listFilters : [])
    .map(cadCollectionFilterSlug)
    .filter(Boolean);
  if (!slugs.length) return rows;
  const wanted = new Set(slugs);
  return rows.filter((row) => wanted.has(row?.project_slug));
}

/**
 * @param {unknown} data
 * @returns {object[]}
 */
export function normalizeCadParcelsForRegistros(data) {
  return (Array.isArray(data) ? data : [])
    .filter((row) => row?.id && row?.project_slug)
    .filter((row) => isCadSellableLot(row))
    .map((row) => ({
      ...row,
      id: String(row.id),
      item_type: CAD_REGISTROS_ITEM_TYPE,
      project_slug: String(row.project_slug),
    }));
}

function activityTime(row) {
  return row?.latest_activity_at ? Date.parse(row.latest_activity_at) : 0;
}

/**
 * Misma lista visual, ordenada por `latest_activity_at`.
 * @param {object[]} lots
 * @param {object[]} cadParcels
 * @param {'recent'|'oldest'} [sortBy]
 * @returns {object[]}
 */
export function mergeRegistrosRows(lots, cadParcels, sortBy = 'recent') {
  const rows = [
    ...(Array.isArray(lots) ? lots : []),
    ...(Array.isArray(cadParcels) ? cadParcels : []),
  ];
  rows.sort((a, b) => {
    const ta = activityTime(a);
    const tb = activityTime(b);
    return sortBy === 'oldest' ? ta - tb : tb - ta;
  });
  return rows;
}
